import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiServices from "../service";
import {
  CustomerDetailsKeyMapping,
  transformKeys,
} from "../utils/DataKeyMapping";

interface ActiveClientState {
  customer_id: string;
  basicDetails: Record<string, any>;
  partnerDetails: Record<string, any>;
  loading: boolean;
  ActiveOnPortal: boolean;
  error: string | null;
}

const initialState: ActiveClientState = {
  customer_id: "",
  basicDetails: {},
  partnerDetails: {},
  loading: false,
  ActiveOnPortal: false,
  error: null,
};

export const getCustomerInfoById = createAsyncThunk<
  Record<string, any>,
  string,
  { rejectValue: string }
>("activeClient/getCustomerInfoById", async (id, { rejectWithValue }) => {
  if (!id) return rejectWithValue("Invalid customer ID");
  try {
    const response: any = await apiServices.client.getDetailByTab(
      id,
      "name_and_address"
    );
    const { isError, data } = response.data;
    if (isError || !data || Object.keys(data).length === 0) {
      return rejectWithValue("Failed to fetch customer details");
    }
    const transformedData = transformKeys(data, CustomerDetailsKeyMapping);
    return {
      customer_id: id,
      ...transformedData[0],
      ActiveOnPortal: transformedData[0].ActiveOnPortal || false, // Ensure ActiveOnPortal is included
    };
  } catch (error: any) {
    return rejectWithValue(error.message || "An error occurred");
  }
});

export const fetchPartnerDetail = createAsyncThunk<
  Record<string, any>,
  string,
  { rejectValue: string }
>("activeClient/fetchPartnerDetail", async (partnerId, { rejectWithValue }) => {
  try {
    const response = await apiServices.client.getDetailByTab(
      partnerId,
      "name_and_address"
    );
    const data = response?.data?.data[0];
    if (!data || Object.keys(data).length === 0) {
      console.warn("No partner details found");
      return rejectWithValue("No partner details found");
    }
    return transformKeys([data], CustomerDetailsKeyMapping)[0];
  } catch (err: any) {
    console.error("Error fetching partner data:", err.message || err);
    return rejectWithValue(err.message || err);
  }
});

export const activeClientSlice = createSlice({
  name: "activeClient",
  initialState,
  reducers: {
    resetActiveClientState: () => initialState,
    updateAssociatedWith: (state, action) => {
      state.basicDetails.associated_with = action.payload;
    },
    setPartnerPortal: (state, action) => {
      state.ActiveOnPortal = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCustomerInfoById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCustomerInfoById.fulfilled, (state, action) => {
        state.loading = false;
        state.customer_id = action.payload.customer_id || "";
        state.basicDetails = action.payload;
      })
      .addCase(getCustomerInfoById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchPartnerDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPartnerDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.partnerDetails = action.payload;
      })
      .addCase(fetchPartnerDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  resetActiveClientState,
  updateAssociatedWith,
  setPartnerPortal,
} = activeClientSlice.actions;

export default activeClientSlice.reducer;
